// components/NetworkGraph.jsx
import { useMemo, useState, useRef } from "react";
import ReactECharts from "echarts-for-react";
import * as d3 from "d3-force";
import {
  Box, Typography, Stack,
  IconButton, Popover, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Checkbox, useTheme, Skeleton, CircularProgress,
} from "@mui/material";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SEV_COLOR, TYPE_COLOR, SEV_ORDER } from "../constants";

// ─── Constants ───────────────────────────────────────────────────────────────
const MIN_SYMBOL_SIZE  = 2;   // floor when the graph is very large
const GROUP_THRESHOLD  = 3;   // /24 peers > this → collapse to triangle
const MAX_DEST_PER_SRC = 25;  // cap individual dest nodes per source to avoid nebulae
const EDGE_GREY        = "#A7A7A7"; // monodirectional src→dest
const EDGE_PURPLE      = "#9171d8"; // bidirectional src↔src (shared destination)

// ─── Helpers ─────────────────────────────────────────────────────────────────
function computeSymbolSize(nodeCount) {
  if (nodeCount <=  5) return 16;
  if (nodeCount <= 15) return 11;
  if (nodeCount <= 30) return 7;
  if (nodeCount <= 60) return 5;
  if (nodeCount <= 120) return 3;
  return MIN_SYMBOL_SIZE;
}

/** Returns the /24 prefix (e.g. "192.168.1") for a given IP string. */
function getSubnet24(ip) {
  if (typeof ip !== "string") return null;
  const p = ip.split(".");
  if (p.length !== 4) return null;
  return `${p[0]}.${p[1]}.${p[2]}`;
}

function edgeScoreColor(score) {
  if (!score || score < 0.3) return EDGE_GREY;
  if (score < 0.6) return "#d48c2e";
  if (score < 0.85) return "#d4562e";
  return "#c0392b";
}

function nodeColor(item, mode) {
  if (mode === "dominant_type") {
    const dom = item.dominant_type ?? item.anomaly_types?.[0] ?? "frequency";
    return TYPE_COLOR[dom] ?? "#888";
  }
  return SEV_COLOR[item.severity_label ?? "info"] ?? "#888";
}

// ─── Layout ──────────────────────────────────────────────────────────────────
function buildGraphOption({ data, clusterBy, severityFilter, showLabels, dark }) {
  // Background colour for hollow source nodes
  const bgPaper = dark ? "#1e1e1c" : "#ffffff";

  // ── 1. Filter & build per-source peer groups ──────────────────────────────
  const srcMeta     = new Map(); // srcId → item
  const srcPeerData = new Map(); // srcId → { individual: string[], grouped: {label,ips,subnet}[] }

  for (const item of data) {
    const src = item.entity?.id;
    if (!src || src === "_global_") continue;
    if (severityFilter.length > 0 && !severityFilter.includes(item.severity_label ?? "info")) continue;

    // New schema: graph.edges[].dst + peak_score — fallback to legacy entity.peers[].dst_ip
    const rawEdges = item.graph?.edges ?? [];
    const edgesData = rawEdges.length > 0
      ? rawEdges.map(e => ({ ip: e.dst, score: e.peak_score ?? 0 })).filter(e => Boolean(e.ip))
      : (item.entity?.peers ?? [])
          .filter(p => p && typeof p === "object" && typeof p.dst_ip === "string")
          .map(p => ({ ip: p.dst_ip, score: 0 }));

    const ipScore = new Map(edgesData.map(e => [e.ip, e.score]));

    srcMeta.set(src, item);

    // Group peers by /24 subnet
    const bySubnet = new Map();
    for (const { ip } of edgesData) {
      const sn = getSubnet24(ip);
      if (!sn) continue;
      if (!bySubnet.has(sn)) bySubnet.set(sn, []);
      bySubnet.get(sn).push(ip);
    }

    const individual = [];
    const grouped    = [];
    for (const [sn, ips] of bySubnet.entries()) {
      if (ips.length > GROUP_THRESHOLD) {
        grouped.push({ label: `${sn}.x`, ips, subnet: sn });
      } else {
        individual.push(...ips);
      }
    }

    // Hard cap: if still too many individual nodes, overflow into a catch-all group
    // This prevents nebulae when all severity levels are active
    if (individual.length > MAX_DEST_PER_SRC) {
      const kept    = individual.slice(0, MAX_DEST_PER_SRC);
      const overflow = individual.slice(MAX_DEST_PER_SRC);
      // merge overflow into an "others" group keyed by source
      grouped.push({ label: `${src}.others`, ips: overflow, subnet: null, isOverflow: true });
      srcPeerData.set(src, { individual: kept, grouped, ipScore });
    } else {
      srcPeerData.set(src, { individual, grouped, ipScore });
    }
  }

  if (srcMeta.size === 0) {
    return {
      backgroundColor: "transparent",
      series: [{ type: "graph", layout: "none", data: [], links: [] }],
    };
  }

  // ── 2. Detect shared destinations (source-source edges) ───────────────────
  const destToSrcs = new Map(); // destKey → Set<srcId>

  for (const [src, { individual, grouped }] of srcPeerData.entries()) {
    for (const ip of individual) {
      if (!destToSrcs.has(ip)) destToSrcs.set(ip, new Set());
      destToSrcs.get(ip).add(src);
    }
    for (const g of grouped) {
      if (!destToSrcs.has(g.label)) destToSrcs.set(g.label, new Set());
      destToSrcs.get(g.label).add(src);
    }
  }

  const sharedPairs = new Map(); // "srcA|||srcB" → count
  for (const [, srcs] of destToSrcs.entries()) {
    if (srcs.size > 1) {
      const arr = [...srcs].sort();
      for (let i = 0; i < arr.length; i++) {
        for (let j = i + 1; j < arr.length; j++) {
          const k = `${arr[i]}|||${arr[j]}`;
          sharedPairs.set(k, (sharedPairs.get(k) ?? 0) + 1);
        }
      }
    }
  }

  // ── 3. Build d3 nodes + links ─────────────────────────────────────────────
  const d3nodes = [];
  const d3links = [];
  const nodeSet = new Set();

  // Place source nodes on a circle for faster convergence
  const srcList  = [...srcMeta.entries()].sort((a, b) =>
    (SEV_ORDER[a[1]?.severity_label] ?? 3) - (SEV_ORDER[b[1]?.severity_label] ?? 3)
  );
  const srcCount = srcList.length;
  const INIT_R   = Math.max(400, srcCount * 55);

  const srcInitPos = new Map();
  srcList.forEach(([src], i) => {
    const angle = (i / srcCount) * 2 * Math.PI;
    srcInitPos.set(src, { x: INIT_R * Math.cos(angle), y: INIT_R * Math.sin(angle) });
  });

  // Source nodes
  for (const [src, item] of srcMeta.entries()) {
    const pos = srcInitPos.get(src) ?? { x: 0, y: 0 };
    d3nodes.push({ id: src, type: "source", item, x: pos.x, y: pos.y });
    nodeSet.add(src);
  }

  // Destination nodes: individual IPs and grouped subnets
  for (const [src, { individual, grouped, ipScore }] of srcPeerData.entries()) {
    const pos = srcInitPos.get(src) ?? { x: 0, y: 0 };

    for (const ip of individual) {
      const isShared = (destToSrcs.get(ip)?.size ?? 0) > 1;
      if (!nodeSet.has(ip)) {
        d3nodes.push({
          id: ip, type: "dest", nodeKind: "ip", isShared,
          x: pos.x + (Math.random() - 0.5) * 70,
          y: pos.y + (Math.random() - 0.5) * 70,
        });
        nodeSet.add(ip);
      }
      d3links.push({ source: src, target: ip, shared: isShared, score: ipScore?.get(ip) ?? 0 });
    }

    for (const g of grouped) {
      const isShared = (destToSrcs.get(g.label)?.size ?? 0) > 1;
      if (!nodeSet.has(g.label)) {
        d3nodes.push({
          id: g.label, type: "dest", nodeKind: "group",
          groupedIps: g.ips, subnet: g.subnet, isShared,
          x: pos.x + (Math.random() - 0.5) * 70,
          y: pos.y + (Math.random() - 0.5) * 70,
        });
        nodeSet.add(g.label);
      }
      const groupScore = g.ips.reduce((mx, ip) => Math.max(mx, ipScore?.get(ip) ?? 0), 0);
      d3links.push({ source: src, target: g.label, shared: isShared, score: groupScore });
    }
  }

  // Source-source dotted edges for shared destinations
  for (const [k, count] of sharedPairs.entries()) {
    const [a, b] = k.split("|||");
    d3links.push({ source: a, target: b, srcSrc: true, count });
  }

  // ── 4. d3-force simulation ───────────────────────────────────────────────
  //
  //  Performance strategy:
  //  • Phase 1 (50 ticks, sources only): place clusters far apart before destinations enter
  //  • Phase 2 (180 ticks, all nodes): Barnes-Hut theta=0.9 + high velocityDecay
  //    for fast convergence; collision keeps dest nodes separated at tiny symbolSize

  // Phase 1 — sources only, fast spread
  const srcOnlyNodes = d3nodes.filter(n => n.type === "source");
  d3.forceSimulation(srcOnlyNodes)
    .force("charge", d3.forceManyBody().strength(-6000).theta(0.9))
    .force("center", d3.forceCenter(0, 0).strength(0.12))
    .velocityDecay(0.5)
    .stop()
    .tick(50);

  // Copy settled positions back (use Map for O(1) lookup, not find)
  const srcPosMap = new Map(srcOnlyNodes.map(n => [n.id, { x: n.x, y: n.y }]));
  for (const n of d3nodes) {
    const p = srcPosMap.get(n.id);
    if (p) { n.x = p.x; n.y = p.y; }
  }

  // Phase 2 — all nodes
  d3.forceSimulation(d3nodes)
    .force("link",
      d3.forceLink(d3links)
        .id(d => d.id)
        .distance(l => l.srcSrc ? 480 : 60)
        .strength(l  => l.srcSrc ? 0.01 : 0.85)
    )
    .force("charge",
      d3.forceManyBody()
        .strength(d => d.type === "source" ? -2800 : -40)
        .theta(0.9)       // Barnes-Hut: faster with minimal accuracy loss
    )
    .force("collide",
      d3.forceCollide()
        .radius(d => d.type === "source" ? 70 : 14)
        .strength(0.9)
        .iterations(2)    // 2 iterations is sufficient and much faster than 3
    )
    .force("center", d3.forceCenter(0, 0).strength(0.01))
    .velocityDecay(0.45)  // higher decay → faster settling → fewer ticks needed
    .stop()
    .tick(180);

  // ── 5. Serialize to ECharts ───────────────────────────────────────────────
  const symbolSize = computeSymbolSize(d3nodes.length);
  const ecNodes = [];
  const ecEdges = [];
  const usedIds = new Set();

  for (const n of d3nodes) {
    if (usedIds.has(n.id)) continue;
    usedIds.add(n.id);

    if (n.type === "source") {
      const item  = n.item;
      const color = nodeColor(item, clusterBy);

      ecNodes.push({
        id:   n.id,
        name: n.id,
        x:    n.x,
        y:    n.y,
        symbolSize: symbolSize,
        symbol: "circle",
        // Source = hollow circle: transparent fill, coloured border
        itemStyle: {
          color:       bgPaper,
          borderColor: color,
          borderWidth: 2.5,
          opacity: 1,
        },
        // emphasis styling is handled at series level (focus: "adjacency")
        emphasis: {
          itemStyle: {
            color:       bgPaper,
            borderColor: color,
            borderWidth: 3.5,
            shadowBlur: 10,
            shadowColor: color + "99",
            opacity: 1,
          },
          label: { show: true, fontSize: 10 },
          scale: false,
        },
        label: {
          show: showLabels,
          formatter: n.id, fontSize: 10, position: "top", distance: 4,
          color: dark ? "#e8e8e8" : "#2a2a2a",
        },
        _nt: "anomaly",
        _item: item,
        _meta: {
          severity: item.severity_label,
          status:   item.status,
          occurrences: item.occurrence_count,
          peakScore: item.peak_score,
          types: (item.anomaly_types ?? []).join(", "),
          peerCount: item.graph?.total_peer_count ?? item.entity?.unique_peer_count,
          portCount: (() => {
            const s = new Set();
            (item.graph?.source?.reasons ?? []).forEach(r => (r.ports ?? []).forEach(p => s.add(p)));
            (item.graph?.edges ?? []).forEach(e => (e.reasons ?? []).forEach(r => (r.ports ?? []).forEach(p => s.add(p))));
            return s.size || item.entity?.unique_port_count;
          })(),
          edgesTruncated: item.graph?.edges_truncated ?? false,
          graphScope:     item.graph_scope ?? null,
        },
      });
    } else {
      // Destination node
      const isGroup = n.nodeKind === "group";
      ecNodes.push({
        id:   n.id,
        name: n.id,
        x:    n.x,
        y:    n.y,
        symbolSize: symbolSize,
        // Grouped (>5 IPs) → triangle, individual IP → filled circle
        symbol: isGroup ? "triangle" : "circle",
        // Destination = filled solid shape
        itemStyle: {
          color:       SEV_COLOR.destination,
          borderColor: "transparent",
          borderWidth: 0,
          opacity: 0.82,
        },
        emphasis: {
          itemStyle: {
            color: "#ffffff",
            borderColor: SEV_COLOR.destination,
            borderWidth: 2,
            shadowBlur: 8,
            shadowColor: SEV_COLOR.destination + "aa",
            opacity: 1,
          },
          label: { show: true, fontSize: 9 },
          scale: false,
        },
        label: {
          // Only show label for grouped nodes (not individual IPs, too noisy)
          show: showLabels && isGroup,
          formatter: n.id,
          fontSize: 9,
          position: "right",
          distance: 3,
          color: dark ? "#e8e8e8" : "#2a2a2a",
        },
        _nt: "dest",
        _meta: {
          isDst:      true,
          nodeKind:   n.nodeKind,
          subnet:     n.subnet,
          groupedIps: n.groupedIps,
          label:      n.id,
        },
      });
    }
  }

  // ECharts' "view" coordinate system maps the data bounding box onto the
  // viewport; when every node shares the same x or y (e.g. a single anomaly
  // with no peers) that box has zero width/height, the resulting transform
  // matrix is singular, matrix.invert() returns null, and ECharts crashes
  // internally trying to copy it. Two invisible anchor nodes pin a non-zero
  // bounding box without affecting the visible layout.
  if (ecNodes.length > 0) {
    // Sanitize first: d3-force can occasionally emit NaN/Infinity under
    // extreme charge values, which would make the bounding box (and the
    // anchors meant to protect it) degenerate too.
    for (const n of ecNodes) {
      if (!Number.isFinite(n.x)) n.x = 0;
      if (!Number.isFinite(n.y)) n.y = 0;
    }
    const xs = ecNodes.map(n => n.x);
    const ys = ecNodes.map(n => n.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const PAD = 50;
    const anchorStyle = {
      symbolSize: 0,
      silent: true,
      itemStyle: { opacity: 0 },
      label: { show: false },
      tooltip: { show: false },
    };
    ecNodes.push(
      { id: "__anchor_min__", name: "__anchor_min__", x: minX - PAD, y: minY - PAD, ...anchorStyle },
      { id: "__anchor_max__", name: "__anchor_max__", x: maxX + PAD, y: maxY + PAD, ...anchorStyle },
    );
  }

  for (const l of d3links) {
    const src = l.source?.id ?? l.source;
    const tgt = l.target?.id ?? l.target;
    const isSrcSrc = Boolean(l.srcSrc);
    ecEdges.push({
      source: src,
      target: tgt,
      _kind:        isSrcSrc ? "shared" : "dest",
      _sharedCount: l.count ?? null,
      lineStyle: {
        color:     isSrcSrc ? EDGE_PURPLE : edgeScoreColor(l.score),
        width:     0.8,
        opacity:   isSrcSrc ? 0.6 : (l.score > 0.3 ? 0.55 : 0.35),
        type:      "solid",
        curveness: isSrcSrc ? 0.3 : 0,
      },
    });
  }

  return {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: dark ? "#2c2c2a" : "#fff",
      borderColor:     dark ? "#555" : "#ddd",
      borderWidth: 0.5,
      textStyle: { color: dark ? "#c2c0b6" : "#3d3d3a", fontSize: 12 },
      formatter(p) {
        // ── Edge tooltip ──────────────────────────────────────────────────────
        if (p.dataType === "edge") {
          const d   = p.data;
          const src = d.source;
          const tgt = d.target;
          const isShared = d._kind === "shared";
          const accent   = isShared ? EDGE_PURPLE : EDGE_GREY;

          if (isShared) {
            return `
              <div style="font-size:12px;line-height:1.7">
                <b style="font-size:11px;color:${accent}">⇌ Bidirectional — shared destinations</b><br/>
                <span style="color:#888;font-size:10px">Anomaly A</span><br/>
                <span style="font-family:monospace">${src}</span><br/>
                <span style="color:#888;font-size:10px;display:block;margin-top:3px">Anomaly B</span>
                <span style="font-family:monospace">${tgt}</span><br/>
                <span style="color:#888;font-size:10px;display:block;margin-top:4px">
                  ${d._sharedCount ?? "?"} subnet/IP in common
                </span>
              </div>`;
          }

          return `
            <div style="font-size:12px;line-height:1.7">
              <b style="font-size:11px;color:${accent}">→ Connection</b><br/>
              <span style="color:#888;font-size:10px">Source (anomaly)</span><br/>
              <span style="font-family:monospace">${src}</span><br/>
              <span style="color:#888;font-size:10px;display:block;margin-top:3px">Destination</span>
              <span style="font-family:monospace">${tgt}</span>
            </div>`;
        }

        // ── Node tooltips ─────────────────────────────────────────────────────
        const m = p.data._meta;
        if (!m) return p.data.name;

        // Destination node tooltip
        if (m.isDst) {
          if (m.nodeKind === "group" && m.groupedIps?.length) {
            const ipList = m.groupedIps
              .slice()
              .sort()
              .map(ip => `<span style="font-family:monospace;font-size:11px">• ${ip}</span>`)
              .join("<br/>");
            return (
              `<b>${m.label}</b>` +
              `<br/><span style="color:#888;font-size:10px">${m.groupedIps.length} grouped IPs</span>` +
              `<br/><div style="margin-top:4px;max-height:160px;overflow-y:auto">${ipList}</div>`
            );
          }
          return `<b style="font-family:monospace">${p.data.name}</b><br/><span style="color:#888;font-size:10px">Destination IP</span>`;
        }

        // Source/anomaly node tooltip
        const sc = SEV_COLOR[m.severity] ?? "#888";
        return `
          <b style="font-size:13px">${p.data.name}</b><br/>
          <span style="background:${sc}22;color:${sc};padding:1px 7px;border-radius:3px;font-size:11px">
            ${(m.severity ?? "").toUpperCase()}
          </span>
          <br/><br/>
          Peak score: <b>${m.peakScore ?? 0}</b> · <b>${(m.occurrences ?? 0).toLocaleString()}</b> occurrences<br/>
          Peers: <b>${m.peerCount ?? "—"}</b> · Ports: <b>${m.portCount || "—"}</b><br/>
          ${m.graphScope ? `<span style="color:#888;font-size:10px">${m.graphScope === "star_in" ? "← Inbound" : "→ Outbound"}</span><br/>` : ""}
          ${m.edgesTruncated ? `<span style="color:#e8a034;font-size:10px">⚠ peers truncated</span><br/>` : ""}
          ${m.types ? `<span style="font-size:11px">${m.types}</span>` : ""}
          <br/><span style="color:#888;font-size:10px">click for details</span>
        `;
      },
    },
    series: [{
      type: "graph",
      layout: "none",
      data: ecNodes,
      links: ecEdges,
      roam: true,
      draggable: true,
      // On hover: dim all non-adjacent nodes/edges strongly, highlight the hovered node + its neighbours
      emphasis: {
        focus: "adjacency",
        scale: false,
        lineStyle: { width: 2, opacity: 0.9 },
      },
      // Non-focused elements become nearly invisible → clear contrast
      blur: {
        itemStyle: { opacity: 0.06 },
        lineStyle: { opacity: 0.05 },
        label: { show: false },
      },
      labelLayout: { hideOverlap: true },
      // No arrowheads on edges
      edgeSymbol: ["none", "none"],
    }],
  };
}

// ─── Component ───────────────────────────────────────────────────────────────
const NetworkGraph = ({
  data: externalData,
  severityFilter = ["high"],
  onOpenDrawer,
  loading = false,
}) => {
  const data = useMemo(() => externalData ?? [], [externalData]);
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";

  const echartsRef = useRef(null);

  const [clusterBy,    setClusterBy]    = useState("severity");
  const [showLabels,   setShowLabels]   = useState(true);
  const [fullscreen] = useState(false);
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);

  const handleFitGraph = () => {
    echartsRef.current?.getEchartsInstance()?.dispatchAction({ type: "restore" });
  };

  const option = useMemo(
    () => buildGraphOption({ data, clusterBy, severityFilter, showLabels, dark }),
    [data, clusterBy, severityFilter, showLabels, dark],
  );

  // ECharts' "view" coordinate system (used by roam) computes a singular
  // transform matrix for a graph series with zero nodes, which crashes
  // internally. Skip mounting the chart entirely when there's nothing to draw.
  const hasData = (option.series?.[0]?.data?.length ?? 0) > 0;

  const onChartEvents = useMemo(() => ({
    click(params) {
      if (params.dataType !== "node") return;
      const { _nt, _item } = params.data;
      if (_nt === "anomaly" && _item) {
        onOpenDrawer?.(_item);
      }
      // Clicking destination/subnet nodes intentionally does nothing
    },
  }), [onOpenDrawer]);

  return (
    <Box sx={{ mb: 2 }}>
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6">Network anomalies map</Typography>
          <IconButton size="small" onClick={e => setInfoAnchorEl(e.currentTarget)}>
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          {/* Color by */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Color by</InputLabel>
            <Select value={clusterBy} label="Color by" onChange={e => setClusterBy(e.target.value)}>
              <MenuItem value="severity">Severity</MenuItem>
              <MenuItem value="dominant_type">Anomaly type</MenuItem>
            </Select>
          </FormControl>

          {/* Labels */}
          <FormControlLabel
            control={<Checkbox checked={showLabels} size="small" onChange={e => setShowLabels(e.target.checked)} />}
            label={<Typography variant="caption">Label</Typography>}
            sx={{ mr: 0 }}
          />
        </Stack>
      </Stack>

      {/* ── Chart ── */}
      <Box sx={{
        border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden",
        position: fullscreen ? "fixed" : "relative",
        ...(fullscreen
          ? { top: 64, left: 0, right: 0, bottom: 0, zIndex: 1300, m: 2, height: "calc(100vh - 96px)" }
          : { height: 600 }),
      }}>
        {loading && !hasData ? (
          // Primo caricamento (o filtri che azzerano temporaneamente i dati):
          // nessun grafo precedente da mostrare, skeleton a piena area —
          // stesso trattamento della lista anomalie sotto.
          <Skeleton variant="rounded" width="100%" height="100%" sx={{ bgcolor: "rgba(255,255,255,0.07)" }} />
        ) : hasData ? (
          <ReactECharts
            ref={echartsRef}
            option={option}
            notMerge={true}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "canvas" }}
            onEvents={onChartEvents}
          />
        ) : (
          <Box sx={{
            width: "100%", height: "100%", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Nessuna anomalia da visualizzare per i filtri selezionati
            </Typography>
          </Box>
        )}

        {loading && hasData && (
          // Refetch con filtri: il grafo precedente resta visibile ma offuscato,
          // altrimenti non c'era alcun segnale di caricamento su questo componente
          // (a differenza della lista sotto, che mostra uno skeleton).
          <Box sx={{
            position: "absolute", inset: 0, zIndex: 5,
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.35)",
          }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {/* fit button */}
        {hasData && (
          <IconButton
            size="small"
            title="Fit all nodes"
            onClick={handleFitGraph}
            sx={{
              position: "absolute",
              bottom: 12,
              right: 12,
              zIndex: 10,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <FitScreenIcon fontSize="small" />
          </IconButton>
        )}

      </Box>

      {/* ── Legend ── */}
      <Stack direction="row" spacing={3} sx={{ mt: 1, ml: 0.5 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{
            width: 8, height: 8, borderRadius: "50%",
            border: "2px solid", borderColor: SEV_COLOR.high,
            bgcolor: "background.paper",
          }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Source (anomaly)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: SEV_COLOR.destination }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Destination IP</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{
            width: 0, height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderBottom: `9px solid ${SEV_COLOR.destination}`,
          }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Group &gt;{GROUP_THRESHOLD} IPs (/24)</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{ width: 20, height: 1.5, bgcolor: EDGE_GREY, opacity: 0.7 }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Monodirectional</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.8}>
          <Box sx={{ width: 20, height: 1.5, bgcolor: EDGE_PURPLE, opacity: 0.85 }} />
          <Typography variant="caption" sx={{ color: "text.secondary" }}>Bidirectional (shared dst)</Typography>
        </Stack>
      </Stack>

      {/* ── Info popover ── */}
      <Popover
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        onClose={() => setInfoAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, maxWidth: 400 }}>
          <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Force layout — cluster per anomaly
          </Typography>
          <Typography variant="body2">
            Each anomaly forms its own cluster: the source (hollow circle) is at the centre,
            exclusive destinations (filled circles) orbit it via d3-force simulation.
            When a /24 subnet has more than {GROUP_THRESHOLD} peers it collapses into a
            triangle node — hover to see the full IP list.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <b style={{ color: EDGE_GREY }}>Grey</b> edges: source → destination (monodirectional).{" "}
            <b style={{ color: EDGE_PURPLE }}>Purple</b> edges: two anomaly sources sharing
            the same destination(s) (bidirectional). IP filter matches both source and destination.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Default: <b>HIGH</b> severity only. Add lower severities to expand the graph.
            Each source is capped at {MAX_DEST_PER_SRC} individual destination nodes to keep the layout readable.
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default NetworkGraph;
